import { useEffect, useRef, useState } from 'react';
import type { ScanStep } from '../api';

const SCAN_STAGES = [
  'Fetching the page + robots/sitemap',
  'Extracting signals (structure, entity, off-page)',
  'Asking AI to read the page (extraction test)',
  'Scoring AIO readiness',
];

/** Roughly how many steps a readiness scan reports (backend runScan + fetcher). */
const EXPECTED_STEPS = 10;

/**
 * Staged progress.
 *
 * With `steps` (the readiness scan) it shows what the server says it is doing,
 * line by line, with a running clock on the current step. Without it, the
 * backend does the work in one request, so we animate through the *real*
 * pipeline stages on a timer and cap at ~90% until the response arrives (parent
 * unmounts this on completion). Pass `stages` to match the operation.
 */
export default function ScanProgress({
  label = 'Scanning',
  stages = SCAN_STAGES,
  steps,
}: {
  label?: string;
  stages?: string[];
  steps?: ScanStep[];
}): JSX.Element {
  if (steps) return <LiveProgress label={label} steps={steps} />;
  return <TimedProgress label={label} stages={stages} />;
}

function TimedProgress({ label, stages }: { label: string; stages: string[] }): JSX.Element {
  const STAGES = stages;
  const [pct, setPct] = useState(6);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setPct((p) => (p < 90 ? p + Math.max(1, Math.round((90 - p) / 12)) : p));
    }, 400);
    const s = setInterval(() => setStage((x) => Math.min(x + 1, STAGES.length - 1)), 1400);
    return () => {
      clearInterval(t);
      clearInterval(s);
    };
  }, []);

  return (
    <div className="w-full max-w-2xl mx-auto border border-warm-dark-2 rounded-xl p-5">
      <Bar label={label} pct={pct} />
      <ul className="mt-3 space-y-1">
        {STAGES.map((s, i) => (
          <li key={i} className="flex items-center gap-2 font-mono text-sm">
            <span style={{ color: i < stage ? '#00FFD4' : i === stage ? '#E0B341' : '#6B6B76' }}>
              {i < stage ? '✓' : i === stage ? '▸' : '·'}
            </span>
            <span className={i <= stage ? 'text-text-primary' : 'text-text-secondary'}>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The bar used to park at 90% for the whole AI read, which is the longest step,
 * so the scan looked frozen exactly when it was busiest. Here every step the
 * server reports is listed, the current one carries its own clock, and after a
 * quiet stretch it says in words that this is normal.
 */
function LiveProgress({ label, steps }: { label: string; steps: ScanStep[] }): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  const started = useRef(Date.now());
  // When the CURRENT step arrived, on our clock — the server's `t` is on its own.
  const stepSince = useRef(Date.now());
  const lastCount = useRef(0);
  if (steps.length !== lastCount.current) {
    lastCount.current = steps.length;
    stepSince.current = Date.now();
  }
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  const total = Math.round((now - started.current) / 1000);
  const onStep = Math.round((now - stepSince.current) / 1000);
  // Each reported step moves the bar; the current one creeps on its own so a
  // long step still visibly advances, without ever claiming to be finished.
  const base = (Math.min(steps.length, EXPECTED_STEPS) / EXPECTED_STEPS) * 85;
  const pct = Math.round(Math.min(97, Math.max(4, base + Math.min(12, onStep / 4))));
  const fmt = (ms: number): string => `${(ms / 1000).toFixed(1)}s`;

  return (
    <div className="w-full max-w-2xl mx-auto border border-warm-dark-2 rounded-xl p-5" aria-live="polite">
      <Bar label={label} pct={pct} right={`${total}s`} />
      <ol className="mt-3 space-y-1.5">
        {steps.length === 0 && (
          <li className="flex items-center gap-2 font-mono text-sm text-text-secondary">
            <Spinner /> Starting the scan…
          </li>
        )}
        {steps.map((s, i) => {
          const current = i === steps.length - 1;
          return (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span className="font-mono text-xs text-text-secondary/70 w-12 shrink-0 text-right pt-0.5">{fmt(s.t)}</span>
              <span className="pt-0.5">{current ? <Spinner /> : <span className="text-cyan">✓</span>}</span>
              <span className={current ? 'text-text-primary' : 'text-text-secondary'}>
                {s.text}
                {current && onStep >= 3 && <span className="font-mono text-xs text-text-secondary"> · {onStep}s</span>}
              </span>
            </li>
          );
        })}
      </ol>
      {onStep >= 12 && (
        <p className="mt-3 text-sm text-text-secondary border-t border-warm-dark-2 pt-3">
          Still working. This step waits on an AI model and can take up to a minute. Please keep this window open.
        </p>
      )}
    </div>
  );
}

function Bar({ label, pct, right }: { label: string; pct: number; right?: string }): JSX.Element {
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-sm text-cyan">{label}…</span>
        <span className="font-mono text-sm text-text-secondary">{right ?? `${pct}%`}</span>
      </div>
      <div className="h-2 rounded bg-warm-dark-2 overflow-hidden">
        <div className="h-full rounded bg-cyan transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />
      </div>
    </>
  );
}

function Spinner(): JSX.Element {
  return <span className="inline-block w-3 h-3 border-2 border-cyan/40 border-t-cyan rounded-full animate-spin" />;
}
